import { ChallengeDifficultyBadge, ChallengeTypeBadge, MaxCompletionsBadge, SpeedBonusBadge } from '@/components/challenges';
import type { EventChallenge, EventChallengeGroup } from '@/config/objects/events';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  ConfirmDialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowRight,
  GripVertical,
  Layers,
  MoreHorizontal,
  Pencil,
  Sparkles,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface UngroupedEventChallengesProps {
  eventId: string;
  challenges: EventChallenge[];
  groups: EventChallengeGroup[];
  onEditChallenge?: (challenge: EventChallenge) => void;
  disabled?: boolean;
}

export function UngroupedEventChallenges({
  eventId,
  challenges,
  groups,
  onEditChallenge,
  disabled = false,
}: UngroupedEventChallengesProps) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [deleteTarget, setDeleteTarget] = useState<EventChallenge | null>(null);

  const removeMutation = useMutation({
    mutationFn: async (challengeId: string) => {
      return apiClient.delete(`/events/${eventId}/challenges/${challengeId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['EventChallenges', eventId] });
      queryClient.invalidateQueries({ queryKey: ['EventGroups', eventId] });
      toast.success('Challenge removed from event');
      setDeleteTarget(null);
    },
    onError: (err: Error) => {
      toast.error(err?.message || 'Failed to remove challenge');
    },
  });

  const moveToGroupMutation = useMutation({
    mutationFn: async ({ challengeId, groupId }: { challengeId: string; groupId: string }) => {
      return apiClient.patch(`/events/${eventId}/challenges/${challengeId}`, {
        groupId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['EventChallenges', eventId] });
      queryClient.invalidateQueries({ queryKey: ['EventGroups', eventId] });
      toast.success('Challenge moved to group');
    },
    onError: (err: Error) => {
      toast.error(err?.message || 'Failed to move challenge');
    },
  });

  const handleMoveToGroup = (challengeId: string, groupId: string) => {
    moveToGroupMutation.mutate({ challengeId, groupId });
  };

  return (
    <>
      <div className="pt-4 border-t mt-6">
        <Card className="rounded-xl border-amber-200 bg-amber-50/50 shadow-sm">
          <CardHeader className="px-4 py-3 border-b border-amber-200">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <div>
                <h3 className="font-semibold text-amber-900">
                  Ungrouped Challenges ({challenges.length})
                </h3>
                <p className="text-sm text-amber-700/80">
                  These challenges need to be assigned to a group
                </p>
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-0">
            <ul className="divide-y divide-amber-200">
              {challenges.map((challenge) => (
                <li
                  key={challenge.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/events/${eventId}/challenges/${challenge.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/events/${eventId}/challenges/${challenge.id}`);
                    }
                  }}
                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-amber-100/50 transition-colors group cursor-pointer focus:bg-amber-100/50 focus:outline-none"
                >
                  {!disabled && (
                    <GripVertical className="h-4 w-4 text-amber-400 opacity-0 group-hover:opacity-100" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-foreground">{challenge.name}</span>
                      {challenge.maxCompletions != null && (
                        <MaxCompletionsBadge maxCompletions={challenge.maxCompletions} />
                      )}
                      <SpeedBonusBadge speedBonuses={challenge.speedBonuses} />
                      {challenge.isCustomized && (
                        <Badge variant="outline" className="text-xs px-1.5 py-0 h-5 gap-1">
                          <Sparkles className="h-3 w-3" />
                          Customized
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <ChallengeTypeBadge type={challenge.type} />
                      <ChallengeDifficultyBadge difficulty={challenge.difficulty} />
                    </div>
                  </div>

                  <span className="shrink-0 font-mono text-sm tabular-nums text-muted-foreground">
                    {challenge.points} pts
                  </span>

                  <div onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {onEditChallenge && (
                        <>
                          <DropdownMenuItem onClick={() => onEditChallenge(challenge)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit Challenge
                          </DropdownMenuItem>
                          {!disabled && <DropdownMenuSeparator />}
                        </>
                      )}
                      {!disabled && (
                        <>
                          {groups.length > 0 && (
                            <>
                              <DropdownMenuSub>
                                <DropdownMenuSubTrigger>
                                  <ArrowRight className="h-4 w-4 mr-2" />
                                  Move to Group
                                </DropdownMenuSubTrigger>
                                <DropdownMenuSubContent>
                                  {groups.map((group) => (
                                    <DropdownMenuItem
                                      key={group.id}
                                      onClick={() => handleMoveToGroup(challenge.id, group.id)}
                                    >
                                      <Layers className="h-4 w-4 mr-2" />
                                      {group.name}
                                    </DropdownMenuItem>
                                  ))}
                                </DropdownMenuSubContent>
                              </DropdownMenuSub>
                              <DropdownMenuSeparator />
                            </>
                          )}
                          <DropdownMenuItem
                            onClick={() => setDeleteTarget(challenge)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Remove from Event
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Remove Challenge"
        description={`Are you sure you want to remove "${deleteTarget?.name}" from this event?`}
        confirmText="Remove"
        variant="destructive"
        loading={removeMutation.isPending}
        onConfirm={() => {
          if (deleteTarget) removeMutation.mutate(deleteTarget.id);
        }}
      />
    </>
  );
}
